import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, User, LogOut, Lock, HelpCircle, ChevronDown, BookOpen, Info, Sun, Zap, Building2, Bug, ListChecks, Bell } from 'lucide-react';
import { useAmoebaConfig } from '../hooks/useAmoebaConfig';
import { useTheme } from '../contexts/ThemeContext';
import { usePendingCounts } from '../hooks/usePendingCounts';
import TimerChip from './TimerChip';
import api from '../services/api';
import { openBugReport } from '../utils/errorCollector';
import { pendingTotal } from '../navigation/pendingChips';

const menuItem = { display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.6rem 0.75rem', textDecoration: 'none', color: 'var(--text-secondary)', fontSize: '0.85rem', borderRadius: '0.5rem' };

const Navbar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [showProfileMenu, setShowProfileMenu] = useState(false);
    const [showHelpMenu, setShowHelpMenu] = useState(false);
    const helpRef = useRef(null);
    const profileRef = useRef(null);

    // Menus close when you click anywhere else, press Escape, or go to another page.
    useEffect(() => {
        if (!showProfileMenu && !showHelpMenu) return undefined;
        const outside = (e) => {
            if (profileRef.current && !profileRef.current.contains(e.target)) setShowProfileMenu(false);
            if (helpRef.current && !helpRef.current.contains(e.target)) setShowHelpMenu(false);
        };
        const escape = (e) => { if (e.key === 'Escape') { setShowProfileMenu(false); setShowHelpMenu(false); } };
        document.addEventListener('mousedown', outside);
        document.addEventListener('keydown', escape);
        return () => { document.removeEventListener('mousedown', outside); document.removeEventListener('keydown', escape); };
    }, [showProfileMenu, showHelpMenu]);
    useEffect(() => { setShowProfileMenu(false); setShowHelpMenu(false); }, [location.pathname]);
    const { config } = useAmoebaConfig();
    const { theme, setTheme } = useTheme();
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userName = user.name || 'User';
    const pending = usePendingCounts(location.pathname); // refetches on every in-app navigation, not just once
    const pendingCount = pendingTotal(pending);

    const isActive = (path) => location.pathname === path;

    const preJoining = user.status === 'PRE_JOINING';
    const navItems = [
        { path: preJoining ? '/welcome' : '/dashboard', icon: Home, label: 'Home' },
        ...(user.isSystemAdmin ? [{ path: '/platform/tenants', icon: Building2, label: 'Platform' }] : []),
    ];

    // End the server session too, not just the browser's flag (the session
    // cookie would otherwise stay valid for days).
    const handleLogout = async () => {
        try { await api.post('/auth/logout'); } catch { /* signing out locally still works */ }
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('user');
        window.location.href = '/';
    };

    const toggleTheme = () => setTheme(theme === 'peach' ? 'turquoise' : 'peach');

    const ThemeIcon = theme === 'turquoise' ? Zap : Sun;
    const themeLabel = theme === 'turquoise' ? 'Turquoise' : 'Peach';

    return (
        <nav style={{
            background: 'var(--navbar-bg)',
            backdropFilter: 'blur(10px)',
            borderBottom: '1px solid var(--border)',
            position: 'sticky',
            top: 0,
            zIndex: 100,
            padding: '0.75rem 0'
        }}>
            <div className="container navbar-inner" style={{ padding: '0 2rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Link to={preJoining ? '/welcome' : '/dashboard'} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <img src={config.orgLogoUrl || "/logo.png"} alt={config.orgName} style={{ height: '60px', objectFit: 'contain' }} />
                </Link>

                <div className="navbar-right" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    {!preJoining && <TimerChip />}
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        {navItems.map((item) => (
                            <Link
                                key={item.path}
                                to={item.path}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    padding: '0.5rem 1rem',
                                    borderRadius: '0.5rem',
                                    textDecoration: 'none',
                                    color: isActive(item.path) ? 'var(--primary)' : 'var(--text-muted)',
                                    background: isActive(item.path) ? 'var(--accent-subtle)' : 'transparent',
                                    fontWeight: isActive(item.path) ? 600 : 500,
                                    transition: 'all 0.2s ease',
                                    fontSize: '0.9rem',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                <item.icon size={16} />
                                {item.label}
                            </Link>
                        ))}

                        {/* Help Dropdown */}
                        <div ref={helpRef} style={{ position: 'relative' }}>
                            <button
                                onClick={() => setShowHelpMenu(!showHelpMenu)}
                                onBlur={() => setTimeout(() => setShowHelpMenu(false), 200)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    padding: '0.5rem 1.1rem',
                                    borderRadius: '0.5rem',
                                    border: 'none',
                                    background: showHelpMenu ? 'var(--accent-subtle)' : 'transparent',
                                    color: showHelpMenu ? 'var(--primary)' : 'var(--text-muted)',
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                    fontSize: '0.9rem',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                <HelpCircle size={16} /> Help <ChevronDown size={14} />
                            </button>

                            {showHelpMenu && (
                                <div style={{
                                    position: 'absolute',
                                    top: '120%',
                                    left: 0,
                                    background: 'var(--surface)',
                                    border: '1px solid var(--border)',
                                    borderRadius: '0.75rem',
                                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                                    padding: '0.5rem',
                                    minWidth: '200px',
                                    zIndex: 1000
                                }}>
                                    <Link
                                        to="/help"
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.75rem',
                                            padding: '0.6rem 0.75rem',
                                            textDecoration: 'none',
                                            color: 'var(--text-secondary)',
                                            fontSize: '0.85rem',
                                            borderRadius: '0.5rem'
                                        }}
                                        onMouseOver={(e) => e.currentTarget.style.background = 'var(--surface-hover)'}
                                        onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                                    >
                                        <BookOpen size={16} color="var(--primary)" /> Help Center
                                    </Link>
                                    {/* onMouseDown: runs before the button's blur closes this menu */}
                                    <button
                                        type="button"
                                        onMouseDown={(e) => { e.preventDefault(); setShowHelpMenu(false); openBugReport(); }}
                                        style={{ ...menuItem, border: 'none', background: 'transparent', width: '100%', cursor: 'pointer', textAlign: 'left' }}
                                        onMouseOver={(e) => e.currentTarget.style.background = 'var(--surface-hover)'}
                                        onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                                    >
                                        <Bug size={16} color="#ef4444" /> Report a bug or suggestion
                                    </button>
                                    {!preJoining && <Link
                                        to="/bugs"
                                        style={menuItem}
                                        onMouseOver={(e) => e.currentTarget.style.background = 'var(--surface-hover)'}
                                        onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                                    >
                                        <ListChecks size={16} color="var(--primary)" /> My reports
                                    </Link>}
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.75rem',
                                        padding: '0.6rem 0.75rem',
                                        color: 'var(--text-secondary)',
                                        fontSize: '0.85rem',
                                        borderRadius: '0.5rem',
                                        cursor: 'default'
                                    }}
                                        title="Version 2.0.0 — JA Technology Labs (Multi-tenant SaaS)"
                                    >
                                        <Info size={16} color="#059669" /> About (v2.0.0)
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Needs your attention: a count of leave/timesheet/expense approvals, documents to sign,
                        tasks and (for HR/Admin/Finance) payroll to approve, same list the Home strip shows. */}
                    <Link
                        to={preJoining ? '/tasks' : '/dashboard'}
                        aria-label={pendingCount > 0 ? `${pendingCount} things need your attention` : 'Nothing needs your attention right now'}
                        title={pendingCount > 0 ? `${pendingCount} things need your attention` : 'Nothing needs your attention right now'}
                        style={{ position: 'relative', display: 'flex', alignItems: 'center', color: 'var(--text-muted)', textDecoration: 'none', padding: '0.4rem' }}
                    >
                        <Bell size={18} />
                        {pendingCount > 0 && (
                            <span style={{
                                position: 'absolute', top: 0, right: 0,
                                background: '#ef4444', color: 'white', borderRadius: '999px',
                                fontSize: '0.65rem', fontWeight: 700, lineHeight: 1,
                                minWidth: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                padding: '0 3px',
                            }}>
                                {pendingCount > 99 ? '99+' : pendingCount}
                            </span>
                        )}
                    </Link>

                    {/* Theme Toggle */}
                    <button
                        onClick={toggleTheme}
                        title={`Switch to ${theme === 'peach' ? 'Turquoise' : 'Peach'} theme`}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.4rem',
                            padding: '0.4rem 0.75rem',
                            borderRadius: '2rem',
                            border: '1px solid var(--border)',
                            background: 'var(--accent-subtle)',
                            color: 'var(--primary)',
                            cursor: 'pointer',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            transition: 'all 0.2s ease',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        <ThemeIcon size={14} />
                        <span className="navbar-hide-label">{themeLabel}</span>
                    </button>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <span className="navbar-hide-label" style={{
                            fontSize: '0.9rem',
                            fontWeight: '500',
                            color: 'var(--text-primary)',
                            whiteSpace: 'nowrap'
                        }}>
                            {userName}
                        </span>
                        <div ref={profileRef} style={{ position: 'relative' }}>
                            <button
                                onClick={() => setShowProfileMenu(!showProfileMenu)}
                                style={{
                                    background: 'var(--bg-secondary)',
                                    border: '1px solid var(--border)',
                                    borderRadius: '50%',
                                    width: '40px',
                                    height: '40px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    color: 'var(--text-secondary)',
                                    transition: 'all 0.2s'
                                }}
                            >
                                <User size={20} />
                            </button>

                            {showProfileMenu && (
                                <div style={{
                                    position: 'absolute',
                                    top: '120%',
                                    right: 0,
                                    background: 'var(--surface)',
                                    border: '1px solid var(--border)',
                                    borderRadius: '0.75rem',
                                    boxShadow: 'var(--shadow)',
                                    padding: '0.5rem',
                                    minWidth: '150px',
                                    display: 'flex',
                                    flexDirection: 'column'
                                }}>
                                    <button
                                        onClick={handleLogout}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                            width: '100%',
                                            padding: '0.5rem 0.75rem',
                                            border: 'none',
                                            background: 'transparent',
                                            color: '#ef4444',
                                            borderRadius: '0.5rem',
                                            cursor: 'pointer',
                                            fontSize: '0.9rem',
                                            fontWeight: 500
                                        }}
                                        onMouseOver={(e) => e.currentTarget.style.background = 'rgba(239,68,68,0.08)'}
                                        onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                                    >
                                        <LogOut size={16} />
                                        Logout
                                    </button>
                                    <hr style={{ margin: '0.4rem 0', border: 'none', borderTop: '1px solid var(--border)' }} />
                                    {!preJoining && <button
                                        onClick={() => { navigate('/profile'); setShowProfileMenu(false); }}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                            width: '100%',
                                            padding: '0.5rem 0.75rem',
                                            border: 'none',
                                            background: 'transparent',
                                            color: 'var(--text-primary)',
                                            borderRadius: '0.5rem',
                                            cursor: 'pointer',
                                            fontSize: '0.9rem',
                                            fontWeight: 500
                                        }}
                                        onMouseOver={(e) => e.currentTarget.style.background = 'var(--surface-hover)'}
                                        onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                                    >
                                        <Lock size={16} />
                                        My Profile
                                    </button>}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </nav>
    );
};

export default Navbar;
